import * as React from 'react';
import { useStore, PLANES, PERKS } from '../state/store';

interface Props { onLaunch: () => void; onBack: () => void; }

const panel: React.CSSProperties = {
  position: 'absolute', inset: 0,
  background: 'linear-gradient(160deg, #0a1622 0%, #0e1f30 100%)',
  color: '#e6eef5', pointerEvents: 'auto',
  display: 'flex', flexDirection: 'column',
  fontFamily: 'inherit', overflow: 'hidden',
};

export function Hangar({ onLaunch, onBack }: Props) {
  // Subscribe so the UI updates as credits are spent.
  const prog = useStore((s) => s.prog);
  const buyPlane = useStore((s) => s.buyPlane);
  const buyPerk = useStore((s) => s.buyPerk);
  const selectPlane = useStore((s) => s.selectPlane);
  const [, force] = React.useReducer((x) => x + 1, 0);

  const credits = prog.credits;

  return (
    <div style={panel}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 4 }}>HANGAR</div>
        <div style={{ fontSize: 16, color: '#ffd24a', fontWeight: 700 }}>◈ {credits.toLocaleString()} cr</div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {/* Planes */}
        <div style={{ fontSize: 13, letterSpacing: 3, opacity: 0.7, margin: '4px 0 8px' }}>AIRCRAFT</div>
        {PLANES.map((p) => {
          const owned = prog.ownedPlanes.includes(p.id);
          const selected = prog.selectedPlane === p.id;
          const canBuy = !owned && credits >= p.cost;
          return (
            <div key={p.id} style={{
              border: `1px solid ${selected ? '#5ad' : 'rgba(255,255,255,0.12)'}`,
              background: selected ? 'rgba(80,170,221,0.12)' : 'rgba(255,255,255,0.04)',
              borderRadius: 8, padding: '10px 12px', marginBottom: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{p.name}{selected ? '  ✓' : ''}</div>
                  <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{p.blurb}</div>
                  <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, letterSpacing: 1 }}>
                    SPD {p.speed} · HP {p.maxHp} · ROLL {p.rollRate.toFixed(1)} · GUN {(1 / p.fireInterval).toFixed(0)}/s
                  </div>
                </div>
                <div>
                  {owned ? (
                    <button
                      disabled={selected}
                      onClick={() => { selectPlane(p.id); force(); }}
                      style={btn(selected ? '#2a3a48' : '#2e6da4')}>
                      {selected ? 'EQUIPPED' : 'EQUIP'}
                    </button>
                  ) : (
                    <button
                      disabled={!canBuy}
                      onClick={() => { if (buyPlane(p.id)) force(); }}
                      style={btn(canBuy ? '#b8860b' : '#3a3a3a')}>
                      ◈ {p.cost.toLocaleString()}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Perks */}
        <div style={{ fontSize: 13, letterSpacing: 3, opacity: 0.7, margin: '14px 0 8px' }}>UPGRADES</div>
        {PERKS.map((perk) => {
          const lvl = prog.perkLevels[perk.id] ?? 0;
          const maxed = lvl >= perk.maxLevel;
          const nextCost = perk.costPerLevel * (lvl + 1);
          const canBuy = !maxed && credits >= nextCost;
          return (
            <div key={perk.id} style={{
              border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)',
              borderRadius: 8, padding: '10px 12px', marginBottom: 8,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{perk.name}</div>
                <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{perk.desc}</div>
                {/* level pips */}
                <div style={{ display: 'flex', gap: 4, marginTop: 5 }}>
                  {Array.from({ length: perk.maxLevel }).map((_, i) => (
                    <span key={i} style={{
                      width: 14, height: 6, borderRadius: 2,
                      background: i < lvl ? '#5ad' : 'rgba(255,255,255,0.18)',
                    }} />
                  ))}
                </div>
              </div>
              <button
                disabled={!canBuy}
                onClick={() => { if (buyPerk(perk.id)) force(); }}
                style={btn(maxed ? '#2a3a48' : canBuy ? '#b8860b' : '#3a3a3a')}>
                {maxed ? 'MAX' : `◈ ${nextCost.toLocaleString()}`}
              </button>
            </div>
          );
        })}
        <div style={{ fontSize: 13, letterSpacing: 3, opacity: 0.7, margin: '14px 0 8px' }}>SETTINGS</div>
        <SettingsRow />
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex', gap: 12, padding: '12px 16px',
        borderTop: '1px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.25)',
      }}>
        <button onClick={onBack} style={{ ...btn('#3a4a58'), flex: 1, padding: '12px' }}>BACK</button>
        <button onClick={onLaunch} style={{ ...btn('#2e8b57'), flex: 2, padding: '12px', fontSize: 16 }}>LAUNCH ▸</button>
      </div>
    </div>
  );
}

/** Persisted toggles: FX quality (drops bloom on weak GPUs), colorblind-safe friend/foe markers
 *  (blue vs orange), and reduced motion (steadies the pulsing/scaling HUD effects). */
function SettingsRow() {
  const fxLite = useStore((s) => s.fxLite);
  const colorblind = useStore((s) => s.colorblind);
  const reducedMotion = useStore((s) => s.reducedMotion);
  const toggle = useStore.getState().toggleSetting;
  const row = (label: string, desc: string, on: boolean, key: 'fxLite' | 'colorblind' | 'reducedMotion') => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 8, padding: '10px 12px', marginBottom: 8,
    }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 1 }}>{label}</div>
        <div style={{ fontSize: 11, opacity: 0.65, marginTop: 2 }}>{desc}</div>
      </div>
      <button
        onClick={() => toggle(key)}
        style={{
          minWidth: 64, padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 13, fontWeight: 800, letterSpacing: 1,
          background: on ? 'rgba(40,140,60,0.85)' : 'rgba(255,255,255,0.1)',
          border: `1px solid ${on ? 'rgba(160,255,180,0.7)' : 'rgba(255,255,255,0.3)'}`,
          color: '#fff',
        }}>
        {on ? 'ON' : 'OFF'}
      </button>
    </div>
  );
  return (
    <div>
      {row('LITE GRAPHICS', 'Disables bloom — smoother on older phones', fxLite, 'fxLite')}
      {row('COLORBLIND MARKERS', 'Friend/foe in blue vs orange instead of green vs red', colorblind, 'colorblind')}
      {row('REDUCED MOTION', 'Steadies pulsing and scaling HUD effects', reducedMotion, 'reducedMotion')}
    </div>
  );
}

function btn(bg: string): React.CSSProperties {
  return {
    background: bg, color: '#fff', border: 'none', borderRadius: 6,
    padding: '8px 14px', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
    letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap',
  };
}
